<template>
  <modal-view title="Upload New Release" @close-modal="$emit('close-modal')">
    <form @submit.prevent="handleUpload" class="space-y-5 p-4">
      <!-- Release Type -->
      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Release Type *
        </label>
        <div class="flex gap-3">
          <label
            v-for="rt in releaseTypes"
            :key="rt.value"
            class="flex-1 cursor-pointer rounded-lg border-2 px-4 py-3 text-center text-sm transition-colors"
            :class="
              form.releaseType === rt.value
                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-400'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:border-gray-500'
            "
          >
            <input
              type="radio"
              v-model="form.releaseType"
              :value="rt.value"
              class="hidden"
            />
            <vue-feather :type="rt.icon" size="18" class="mx-auto mb-1" />
            <div class="font-medium">{{ rt.label }}</div>
            <div class="text-xs opacity-75">{{ rt.hint }}</div>
          </label>
        </div>
      </div>

      <!-- Release Target -->
      <div class="grid gap-4 sm:grid-cols-2">
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Product *
          </label>
          <select
            v-model="form.product_code"
            required
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
          >
            <option
              v-for="option in productOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div>
          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Flavor *
          </label>
          <select
            v-model="form.flavor_code"
            required
            class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
          >
            <option
              v-for="option in flavorOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Platform (moved up for store releases) -->
      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Platform *
        </label>
        <select
          v-model="form.platform"
          required
          class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200"
        >
          <option value="" disabled>Select platform</option>
          <option v-for="p in platforms" :key="p" :value="p">{{ p }}</option>
        </select>
      </div>

      <!-- Version -->
      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Version *
        </label>
        <input
          v-model="form.version"
          type="text"
          placeholder="e.g. 2.1.0 or 2.1.0+3"
          required
          @blur="checkDuplicate"
          class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          :class="{ 'border-red-400': duplicateBlocking }"
        />
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Semver format. Use +N for build numbers (e.g. 2.1.0+2)
        </p>
        <p
          v-if="duplicateWarning"
          class="mt-1 text-xs dark:text-amber-400"
          :class="
            duplicateBlocking
              ? 'text-amber-600'
              : 'text-blue-600 dark:text-blue-400'
          "
        >
          {{ duplicateWarning }}
        </p>
      </div>

      <!-- File Input (only for artifact releases) -->
      <div v-if="isArtifactRelease">
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Artifact File *
        </label>
        <div
          class="relative flex items-center justify-center rounded-lg border-2 border-dashed px-6 py-8"
          :class="
            dragOver
              ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600'
          "
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onFileDrop"
        >
          <div class="text-center">
            <vue-feather
              type="upload-cloud"
              size="36"
              class="mx-auto text-gray-400"
            />
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              <button
                type="button"
                @click="$refs.fileInput.click()"
                class="font-medium text-blue-600 hover:text-blue-500"
              >
                Choose file
              </button>
              or drag and drop
            </p>
            <p class="mt-1 text-xs text-gray-500">
              .exe, .msi, .dmg, .pkg, .appimage, .deb, .apk, .aab, .ipa,
              .zip, .tar.gz (max 500MB)
            </p>
            <p
              v-if="selectedFile"
              class="mt-2 text-sm font-medium text-green-600 dark:text-green-400"
            >
              {{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})
            </p>
          </div>
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            accept=".exe,.msi,.dmg,.pkg,.appimage,.deb,.zip,.tar.gz"
            @change="onFileSelect"
          />
        </div>
      </div>

      <!-- Release Notes -->
      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Release Notes
        </label>
        <textarea
          v-model="form.release_notes"
          rows="4"
          placeholder="What's new in this release..."
          class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
        ></textarea>
      </div>

      <!-- Checkboxes -->
      <div class="flex flex-wrap items-center gap-6">
        <label
          class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <input
            type="checkbox"
            v-model="form.is_required"
            class="rounded border-gray-300"
          />
          Required update
        </label>
        <label
          class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <input
            type="checkbox"
            v-model="form.is_published"
            class="rounded border-gray-300"
          />
          Publish immediately
        </label>
      </div>

      <!-- Device Version Options -->
      <div
        class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
      >
        <p
          class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3"
        >
          Device Version Table
        </p>
        <div class="flex flex-wrap items-center gap-6">
          <label
            class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
          >
            <input
              type="checkbox"
              v-model="form.update_current_version"
              class="rounded border-gray-300"
            />
            Update current version
          </label>
          <label
            class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
          >
            <input
              type="checkbox"
              v-model="form.set_minimum_required_version"
              class="rounded border-gray-300"
            />
            Set as minimum required version
          </label>
        </div>
        <p class="text-xs text-gray-400 mt-2">
          Automatically updates the {{ selectedTargetLabel }} device version
          entry for {{ form.platform || "the selected platform" }}.
        </p>
      </div>

      <!-- Error -->
      <p v-if="errorMessage" class="text-sm text-red-600 dark:text-red-400">
        {{ errorMessage }}
      </p>

      <!-- Submit -->
      <div class="flex justify-end gap-3 pt-2">
        <button
          type="button"
          @click="$emit('close-modal')"
          class="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="submit"
          :disabled="loadingUpload || !isValid || duplicateBlocking"
          class="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <vue-feather
            v-if="loadingUpload"
            type="loader"
            size="16"
            class="animate-spin inline-block mr-1"
          />
          {{
            loadingUpload
              ? "Uploading..."
              : isArtifactRelease
              ? "Upload Release"
              : "Create Release"
          }}
        </button>
      </div>
    </form>
  </modal-view>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ModalView from "@/components/modals/ModalView.vue";
import {
  RELEASE_FLAVORS,
  RELEASE_PRODUCTS,
  releaseTargetLabel,
} from "@/constants/releaseTargets";

export default {
  name: "UploadReleaseModal",
  components: { ModalView },
  emits: ["close-modal", "release-uploaded"],
  data() {
    return {
      selectedFile: null,
      dragOver: false,
      errorMessage: "",
      duplicateWarning: "",
      duplicateBlocking: false,
      checkTimeout: null,
      form: {
        releaseType: "artifact",
        product_code: "HOVASTORE",
        flavor_code: "BASE",
        version: "",
        platform: "",
        release_notes: "",
        is_required: false,
        is_published: false,
        update_current_version: true,
        set_minimum_required_version: false,
      },
      releaseTypes: [
        {
          value: "artifact",
          label: "Direct Download",
          hint: "Upload binary artifact",
          icon: "hard-drive",
        },
        {
          value: "store",
          label: "App Store / Play Store",
          hint: "Metadata only, no file upload",
          icon: "shopping-bag",
        },
      ],
      platforms: ["WINDOWS", "MAC", "LINUX", "ANDROID", "IOS"],
      productOptions: RELEASE_PRODUCTS,
      flavorOptions: RELEASE_FLAVORS,
    };
  },
  computed: {
    ...mapGetters("adminReleases", ["loadingUpload"]),
    isArtifactRelease() {
      return this.form.releaseType === "artifact";
    },
    selectedTargetLabel() {
      return releaseTargetLabel(this.form);
    },
    isValid() {
      const base =
        this.form.product_code &&
        this.form.flavor_code &&
        this.form.version &&
        this.form.platform;
      if (this.isArtifactRelease) {
        return base && this.selectedFile;
      }
      return base;
    },
  },
  watch: {
    "form.version"() {
      this.duplicateWarning = "";
      this.duplicateBlocking = false;
    },
    "form.platform"() {
      this.duplicateWarning = "";
      this.duplicateBlocking = false;
      if (this.form.version && this.form.platform) {
        this.checkDuplicate();
      }
    },
    "form.product_code"() {
      this.resetDuplicateCheck();
    },
    "form.flavor_code"() {
      this.resetDuplicateCheck();
    },
  },
  methods: {
    ...mapActions("adminReleases", [
      "uploadRelease",
      "createReleaseMetadata",
      "checkReleaseExists",
    ]),
    resetDuplicateCheck() {
      this.duplicateWarning = "";
      this.duplicateBlocking = false;
      if (this.form.version && this.form.platform) {
        this.checkDuplicate();
      }
    },
    inferPlatform(filename) {
      const name = filename.toLowerCase();
      if (name.endsWith(".dmg") || name.endsWith(".pkg")) return "MAC";
      if (name.endsWith(".exe") || name.endsWith(".msi")) return "WINDOWS";
      if (name.endsWith(".appimage") || name.endsWith(".deb")) return "LINUX";
      if (name.endsWith(".apk") || name.endsWith(".aab")) return "ANDROID";
      if (name.endsWith(".ipa")) return "IOS";
      return "";
    },
    onFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        const inferred = this.inferPlatform(file.name);
        if (inferred) this.form.platform = inferred;
      }
    },
    onFileDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        this.selectedFile = file;
        const inferred = this.inferPlatform(file.name);
        if (inferred) this.form.platform = inferred;
      }
    },
    async checkDuplicate() {
      if (!this.form.version || !this.form.platform) return;
      try {
        const result = await this.checkReleaseExists({
          version: this.form.version,
          platform: this.form.platform,
          product: this.form.product_code,
          flavor: this.form.flavor_code,
        });
        const v = this.form.version;
        const base = v.includes("+") ? v.split("+")[0] : v;
        const build = v.includes("+") ? v.split("+")[1] : "1";
        const suggestion = `${base}+${build}a`;
        if (result?.exists || result?.deleted_exists) {
          this.duplicateWarning = `v${v} for ${this.selectedTargetLabel} / ${this.form.platform} already exists. Each version+platform+target must be unique. Try appending a letter to the build (e.g. ${suggestion}).`;
          this.duplicateBlocking = true;
        } else {
          this.duplicateWarning = "";
          this.duplicateBlocking = false;
        }
      } catch {
        // Silently ignore check errors; the upload will still validate
      }
    },
    async handleUpload() {
      this.errorMessage = "";
      if (!this.isValid) return;

      try {
        let result;
        if (this.isArtifactRelease) {
          const formData = new FormData();
          formData.append("artifact", this.selectedFile);
          formData.append("version", this.form.version);
          formData.append("platform", this.form.platform);
          formData.append("product_code", this.form.product_code);
          formData.append("flavor_code", this.form.flavor_code);
          formData.append("release_notes", this.form.release_notes);
          formData.append("is_required", String(this.form.is_required));
          formData.append("is_published", String(this.form.is_published));
          formData.append(
            "update_current_version",
            String(this.form.update_current_version),
          );
          formData.append(
            "set_minimum_required_version",
            String(this.form.set_minimum_required_version),
          );
          result = await this.uploadRelease(formData);
        } else {
          result = await this.createReleaseMetadata({
            version: this.form.version,
            platform: this.form.platform,
            product_code: this.form.product_code,
            flavor_code: this.form.flavor_code,
            release_notes: this.form.release_notes,
            is_required: this.form.is_required,
            is_published: this.form.is_published,
            update_current_version: this.form.update_current_version,
            set_minimum_required_version:
              this.form.set_minimum_required_version,
          });
        }
        this.$emit("release-uploaded", result || {});
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message || "Upload failed. Please try again.";
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },
  },
};
</script>
